import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
  useTheme,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../../../components/Header';
import ConfirmDeleteDialog from '../../../components/ConfirmDeleteDialog';
import { tokens } from '../../../theme';
import { DataGrid } from '@mui/x-data-grid';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PlaceIcon from '@mui/icons-material/Place';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PaidIcon from '@mui/icons-material/Paid';
import PublicIcon from '@mui/icons-material/Public';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import NotesIcon from '@mui/icons-material/Notes';
import HotelIcon from '@mui/icons-material/Hotel';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import CakeIcon from '@mui/icons-material/Cake';
import WcIcon from '@mui/icons-material/Wc';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { QrCode, VisibilityOff, Visibility as VisibilityIcon } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import Snackbar from '@mui/material/Snackbar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

const BookingInformation = () => {
  const { id } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');

  /** Same thumbnail treatment for Packages + Excursions grids: centered, fixed box, no stretch/squash. */
  const renderGridThumbnail = (row, fallbackIcon = null) => {
    const src = row.image;
    const hasSrc = Boolean(src);
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          py: 0.5,
        }}
      >
        <Avatar
          variant="rounded"
          alt={row.name || ''}
          src={hasSrc ? src : undefined}
          sx={{
            width: 48,
            height: 48,
            flexShrink: 0,
            bgcolor: alpha(colors.grey[700], 0.4),
            '& .MuiAvatar-img': {
              objectFit: 'contain',
            },
          }}
        >
          {!hasSrc && fallbackIcon}
        </Avatar>
      </Box>
    );
  };

  const [booking, setBooking] = useState(null);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [excursionToDelete, setExcursionToDelete] = useState(null);
  const [addPackageDialogOpen, setAddPackageDialogOpen] = useState(false);
  const [addPackageLoading, setAddPackageLoading] = useState(false);
  const [availablePackages, setAvailablePackages] = useState([]);
  const [selectedPackageIdsForDialog, setSelectedPackageIdsForDialog] = useState([]);
  const [addExcursionDialogOpen, setAddExcursionDialogOpen] = useState(false);
  const [availableExcursions, setAvailableExcursions] = useState([]);
  const [selectedExcursions, setSelectedExcursions] = useState([]);
  const [selectedExcursionQtyById, setSelectedExcursionQtyById] = useState({});
  const [selectedPackageQtyById, setSelectedPackageQtyById] = useState({});
  const [deletePackageDialogOpen, setDeletePackageDialogOpen] = useState(false);
  const [packageIdToDelete, setPackageIdToDelete] = useState(null);

  const getBookingDetails = async () => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/bookings/${id}`, { headers: { token } });
    const data = await res.json();
    setBooking(data.data);
  };

  useEffect(() => {
    getBookingDetails();
  }, []);

  // Function to convert backend date to 'YYYY-MM-DD' format
  const convertDate = (backendDate) => {
    const dateObject = new Date(backendDate);
    const day = String(dateObject.getDate()).padStart(2, '0'); // Get day and add leading zero if needed
    const month = String(dateObject.getMonth() + 1).padStart(2, '0'); // Get month (0-based) and add leading zero if needed
    const year = dateObject.getFullYear(); // Get year
    return `${day}-${month}-${year}`; // Return in 'DD-MM-YYYY' format
  };

  const formatDateForCreatedAt = (date) => {
    if (!date) {
      return '';
    }
    const d = new Date(date);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleRemoveExcursion = async (excursionId) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/admin/bookings/${booking.BookingID || booking.bookingId || booking.id
        }/excursions/${excursionId}`,
        {
          method: 'DELETE',
          headers: { token: localStorage.getItem('token') },
        }
      );
      const data = await res.json();
      if (data.success) {
        setBooking((prev) => ({
          ...prev,
          excursions: prev.excursions.filter((e) => e.excursionId !== excursionId),
        }));
        setOpenToast(true);
        setToastmessage('Excursion removed');
      } else {
        setOpenToast(true);
        setToastmessage(data.message || 'Failed to remove excursion');
      }
    } catch (err) {
      setOpenToast(true);
      setToastmessage('Error removing excursion');
    }
  };

  const getAvailableExcursions = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/bookings/${id}/available-excursions`, { headers: { token } });
      const data = await res.json();
      if (data.success) {
        setAvailableExcursions(data.data);
      }
    } catch (error) {
      console.error("Error fetching available excursions:", error);
    }
  };

  const handleAddExcursion = async () => {
    if (selectedExcursions.length === 0) return;
    const payloadItems = selectedExcursions.map((eid) => ({
      excursionId: eid,
      quantity: Math.max(1, Number(selectedExcursionQtyById[eid] || 1)),
    }));
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/bookings/${id}/excursions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          token
        },
        body: JSON.stringify({ excursionIds: payloadItems })
      });
      const data = await res.json();
      if (data.success) {
        setOpenToast(true);
        setToastmessage(data.message);
        setAddExcursionDialogOpen(false);
        setSelectedExcursions([]);
        setSelectedExcursionQtyById({});
        getBookingDetails(); // Refresh list
      } else {
        setOpenToast(true);
        setToastmessage(data.message || 'Failed to add excursions');
      }
    } catch (error) {
      console.error("Error adding excursions:", error);
      setOpenToast(true);
      setToastmessage('Error adding excursions');
    }
  };

  const handleToggleExcursion = (excursionId) => {
    setSelectedExcursions((prev) => {
      const exists = prev.includes(excursionId);
      if (exists) {
        setSelectedExcursionQtyById((qtyPrev) => {
          const next = { ...qtyPrev };
          delete next[excursionId];
          return next;
        });
        return prev.filter((x) => x !== excursionId);
      }
      setSelectedExcursionQtyById((qtyPrev) => ({ ...qtyPrev, [excursionId]: Number(qtyPrev[excursionId] || 1) }));
      return [...prev, excursionId];
    });
  };

  const getAvailablePackagesForBooking = async () => {
    if (!booking?.destinationId) return [];
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/countrypackages/${booking.destinationId}`, {
        headers: { token: localStorage.getItem('token') },
      });
      const data = await res.json();
      if (data.success && data.packages) {
        setAvailablePackages(data.packages);
        return data.packages;
      }
      setAvailablePackages([]);
      return [];
    } catch (error) {
      console.error('Error fetching packages:', error);
      setAvailablePackages([]);
      return [];
    }
  };

  const openAddPackageDialog = async () => {
    await getAvailablePackagesForBooking();
    let initial = [];
    let initialQty = {};
    if (booking?.packages?.length) {
      initial = booking.packages.map((bp) => bp.packageId);
      initialQty = booking.packages.reduce((acc, bp) => {
        acc[bp.packageId] = Math.max(1, Number(bp.totalQuantity || 1));
        return acc;
      }, {});
    } else if (booking?.packageIds?.length) {
      initial = [...booking.packageIds];
    } else if (booking?.packageId) {
      initial = [booking.packageId];
    }
    if (!Object.keys(initialQty).length) {
      initial.forEach((pid) => {
        initialQty[pid] = 1;
      });
    }
    setSelectedPackageIdsForDialog(initial);
    setSelectedPackageQtyById(initialQty);
    setAddPackageDialogOpen(true);
  };

  const handleTogglePackageInDialog = (packageId) => {
    const n = Number(packageId);
    setSelectedPackageIdsForDialog((prev) => {
      const has = prev.some((pid) => Number(pid) === n);
      if (has) {
        setSelectedPackageQtyById((qtyPrev) => {
          const next = { ...qtyPrev };
          delete next[n];
          return next;
        });
        return prev.filter((pid) => Number(pid) !== n);
      }
      setSelectedPackageQtyById((qtyPrev) => ({ ...qtyPrev, [n]: Number(qtyPrev[n] || 1) }));
      return [...prev, n];
    });
  };

  const handleSavePackagesFromDialog = async () => {
    const bid = booking.BookingID || booking.bookingId || booking.id || id;
    const hadPackages = Boolean(booking?.packages?.length || booking?.packageId);

    if (selectedPackageIdsForDialog.length === 0) {
      if (!hadPackages) {
        setAddPackageDialogOpen(false);
        return;
      }
      setAddPackageLoading(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/bookings/${bid}/remove-package`, {
          method: 'POST',
          headers: { token: localStorage.getItem('token') },
        });
        const data = await res.json();
        if (data.success) {
          setOpenToast(true);
          setToastmessage(data.message || 'Packages removed');
          setAddPackageDialogOpen(false);
          getBookingDetails();
        } else {
          setOpenToast(true);
          setToastmessage(data.message || 'Failed to remove packages');
        }
      } catch {
        setOpenToast(true);
        setToastmessage('Error removing packages');
      } finally {
        setAddPackageLoading(false);
      }
      return;
    }

    setAddPackageLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/bookings/${bid}/assign-package`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          token: localStorage.getItem('token'),
        },
        body: JSON.stringify({
          packageItems: selectedPackageIdsForDialog.map((pid) => ({
            packageId: Number(pid),
            quantity: Math.max(1, Number(selectedPackageQtyById[pid] || 1)),
          })),
        }),
      });
      const data = await res.json();
      if (data.success) {
        setOpenToast(true);
        setToastmessage(data.message || 'Packages updated');
        setAddPackageDialogOpen(false);
        setSelectedPackageIdsForDialog([]);
        setSelectedPackageQtyById({});
        getBookingDetails();
      } else {
        setOpenToast(true);
        setToastmessage(data.message || 'Failed to update packages');
      }
    } catch {
      setOpenToast(true);
      setToastmessage('Error updating packages');
    } finally {
      setAddPackageLoading(false);
    }
  };

  const handleDeletePackageRow = async (packageId) => {
    const ids =
      booking?.packages?.map((p) => p.packageId) || (booking?.packageId ? [booking.packageId] : []);
    const next = ids.filter((pid) => pid !== packageId);
    const bid = booking.BookingID || booking.bookingId || booking.id || id;
    setAddPackageLoading(true);
    try {
      if (next.length === 0) {
        const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/bookings/${bid}/remove-package`, {
          method: 'POST',
          headers: { token: localStorage.getItem('token') },
        });
        const data = await res.json();
        if (data.success) {
          setOpenToast(true);
          setToastmessage(data.message || 'Package removed');
          getBookingDetails();
        } else {
          setOpenToast(true);
          setToastmessage(data.message || 'Failed to remove');
        }
      } else {
        const qtyMap = (booking?.packages || []).reduce((acc, p) => {
          acc[p.packageId] = Math.max(1, Number(p.totalQuantity || 1));
          return acc;
        }, {});
        const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/bookings/${bid}/assign-package`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            token: localStorage.getItem('token'),
          },
          body: JSON.stringify({
            packageItems: next.map((pid) => ({
              packageId: Number(pid),
              quantity: Math.max(1, Number(qtyMap[pid] || 1)),
            })),
          }),
        });
        const data = await res.json();
        if (data.success) {
          setOpenToast(true);
          setToastmessage('Package removed');
          getBookingDetails();
        } else {
          setOpenToast(true);
          setToastmessage(data.message || 'Failed to update');
        }
      }
    } catch {
      setOpenToast(true);
      setToastmessage('Error removing package');
    } finally {
      setAddPackageLoading(false);
    }
  };

  const openDeletePackageDialog = (packageId) => {
    setPackageIdToDelete(packageId);
    setDeletePackageDialogOpen(true);
  };

  const closeDeletePackageDialog = () => {
    setDeletePackageDialogOpen(false);
    setPackageIdToDelete(null);
  };

  const confirmDeletePackage = async () => {
    if (packageIdToDelete == null) return;
    await handleDeletePackageRow(packageIdToDelete);
    closeDeletePackageDialog();
  };

  const handleTogglePackageGratis = async (bookingId, packageId, currentStatus, editable) => {
    if (!editable) {
      setOpenToast(true);
      setToastmessage('Save packages via booking_packages to enable package Gratis toggle');
      return;
    }
    try {
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/admin/bookings/${bookingId}/packages/${packageId}/toggle-gratis`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            token: localStorage.getItem('token'),
          },
          body: JSON.stringify({ isGratis: !currentStatus }),
        }
      );
      const data = await res.json();
      if (data.success) {
        setOpenToast(true);
        setToastmessage('Package gratis status updated');
        getBookingDetails();
      } else {
        setOpenToast(true);
        setToastmessage(data.message || 'Failed to update package gratis');
      }
    } catch (error) {
      console.error('Error toggling package gratis:', error);
      setOpenToast(true);
      setToastmessage('Error updating package gratis');
    }
  };

  const [regeneratingQr, setRegeneratingQr] = useState(false);

  const handleRegenerateQrCodes = async () => {
    setRegeneratingQr(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/bookings/${id}/regenerate-qr-codes`, {
        method: 'POST',
        headers: { token },
      });
      const data = await res.json();
      setOpenToast(true);
      setToastmessage(data.message || (data.success ? 'QR codes regenerated' : 'Failed to regenerate QR codes'));
      if (data.success) {
        getBookingDetails();
      }
    } catch (error) {
      console.error('Error regenerating QR codes:', error);
      setOpenToast(true);
      setToastmessage('Error regenerating QR codes');
    } finally {
      setRegeneratingQr(false);
    }
  };

  const handleToggleGratis = async (bookingId, excursionId, currentStatus) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/admin/bookings/${bookingId}/excursions/${excursionId}/toggle-gratis`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            token: localStorage.getItem('token'),
          },
          body: JSON.stringify({ isGratis: !currentStatus }),
        }
      );
      const data = await res.json();
      if (data.success) {
        setOpenToast(true);
        setToastmessage('Gratis status updated successfully');
        getBookingDetails(); // Refresh list
      } else {
        setOpenToast(true);
        setToastmessage(data.message || 'Failed to update gratis status');
      }
    } catch (error) {
      console.error('Error toggling gratis status:', error);
      setOpenToast(true);
      setToastmessage('Error updating gratis status');
    }
  };

  return (
    <>
      <Box m="20px">
        <Box display="flex" justifyContent="space-between" alignItems="center" rowGap="10px" flexWrap="wrap-reverse">
          <Header title={`Booking #${id}`} subtitle={`Created at ${formatDateForCreatedAt(booking?.createdAt)}`} />

          <Box display="flex" gap="10px">
            <Button
              onClick={handleRegenerateQrCodes}
              disabled={regeneratingQr}
              sx={{
                backgroundColor: colors.greenAccent[700],
                color: colors.grey[100],
                fontSize: '14px',
                fontWeight: 'bold',
                padding: '10px 20px',
                '&:hover': {
                  backgroundColor: colors.greenAccent[800],
                },
              }}
            >
              {regeneratingQr ? 'Regenerating...' : 'Regenerate QR Codes'}
            </Button>
            <Link to={-1}>
              <Button
                sx={{
                  backgroundColor: colors.blueAccent[700],
                  color: colors.grey[100],
                  fontSize: '14px',
                  fontWeight: 'bold',
                  padding: '10px 20px',
                  '&:hover': {
                    backgroundColor: colors.blueAccent[800],
                  },
                }}
              >
                Back
              </Button>
            </Link>
          </Box>
        </Box>

        {booking && (
          <Box backgroundColor={colors.primary[400]} borderRadius={5} p="1.5rem">
            <Box display="flex" flexDirection="column" gap="1.5rem">
              {/* booking Details */}
              <Box display="flex" flexDirection="column" gap="10px">
                <Typography variant="h3" fontWeight={'bold'} color={colors.grey[100]}>
                  Booking Details
                </Typography>
                <Divider variant="fullWidth" />

                <Typography variant="h5" color={colors.greenAccent[500]} display="flex" alignItems="center" gap="5px">
                  <PublicIcon fontSize="small" /> Destination:{' '}
                  <span style={{ color: colors.grey[100] }}>{booking?.destination}</span>
                </Typography>
                <Typography variant="h5" color={colors.greenAccent[500]} display="flex" alignItems="center" gap="5px">
                  <CalendarTodayIcon fontSize="small" /> Booking Date:{' '}
                  <span style={{ color: colors.grey[100] }}>{convertDate(booking?.bookingStartDate)}</span>
                </Typography>
                <Typography variant="h5" color={colors.greenAccent[500]} display="flex" alignItems="center" gap="5px">
                  <BookmarkIcon fontSize="small" /> Contract No:{' '}
                  <span style={{ color: colors.grey[100] }}>{booking?.contractNo}</span>
                </Typography>
                <Typography variant="h5" color={colors.greenAccent[500]} display="flex" alignItems="center" gap="5px">
                  <PublicIcon fontSize="small" /> Starting Point:{' '}
                  <span style={{ color: colors.grey[100] }}>{booking?.startDestination}</span>
                </Typography>
                <Typography variant="h5" color={colors.greenAccent[500]} display="flex" alignItems="center" gap="5px">
                  <DirectionsBusIcon fontSize="small" /> Bus:{' '}
                  <span style={{ color: colors.grey[100] }}>{booking?.bus}</span>
                </Typography>
                <Typography variant="h5" color={colors.greenAccent[500]} display="flex" alignItems="center" gap="5px">
                  <HotelIcon fontSize="small" /> Hotel:{' '}
                  <span style={{ color: colors.grey[100] }}>{booking?.hotel}</span>
                </Typography>
                <Typography variant="h5" color={colors.greenAccent[500]} display="flex" alignItems="center" gap="5px">
                  <CorporateFareIcon fontSize="small" /> Partner:{' '}
                  <span style={{ color: colors.grey[100] }}>{booking?.partner}</span>
                </Typography>
              </Box>

              {/* Passenger & Guide Details */}
              <Box display="flex" flexDirection={{ xs: 'column', lg: 'row' }} gap="2rem">
                <Stack spacing={1} flex={1}>
                  <Typography variant="h3" fontWeight={'bold'} color={colors.grey[100]}>
                    Passenger Details
                  </Typography>
                  <Divider variant="fullWidth" />

                  <Typography variant="h5" color={colors.greenAccent[500]} display="flex" alignItems="center" gap="5px">
                    <PersonIcon fontSize="small" /> Name:{' '}
                    <span style={{ color: colors.grey[100] }}>
                      {booking?.passenger?.name} {booking?.passenger?.surname}
                    </span>
                  </Typography>
                  <Typography variant="h5" color={colors.greenAccent[500]} display="flex" alignItems="center" gap="5px">
                    <MailOutlineIcon fontSize="small" /> Email:{' '}
                    <span style={{ color: colors.grey[100] }}>{booking?.passenger?.email}</span>
                  </Typography>
                  <Typography variant="h5" color={colors.greenAccent[500]} display="flex" alignItems="center" gap="5px">
                    <LocalPhoneIcon fontSize="small" /> Phone:{' '}
                    <span style={{ color: colors.grey[100] }}>{booking?.passenger?.phone}</span>
                  </Typography>
                  <Typography variant="h5" color={colors.greenAccent[500]} display="flex" alignItems="center" gap="5px">
                    <BadgeIcon fontSize="small" /> Gender:{' '}
                    <span style={{ color: colors.grey[100] }}>
                      {booking?.passenger?.gender === 'm'
                        ? 'Male'
                        : booking?.passenger?.gender === 'f'
                          ? 'Female'
                          : 'Other'}
                    </span>
                  </Typography>
                  <Typography variant="h5" color={colors.greenAccent[500]} display="flex" alignItems="center" gap="5px">
                    <CakeIcon fontSize="small" /> Date of Birth:{' '}
                    <span style={{ color: colors.grey[100] }}>{booking?.passenger?.dob}</span>
                  </Typography>
                  <Typography variant="h5" color={colors.greenAccent[500]} display="flex" alignItems="center" gap="5px">
                    <PlaceIcon fontSize="small" /> Address:{' '}
                    <span style={{ color: colors.grey[100] }}>{booking?.passenger?.address}</span>
                  </Typography>
                  <Typography variant="h5" color={colors.greenAccent[500]} display="flex" alignItems="center" gap="5px">
                    <MenuBookIcon fontSize="small" /> Passport:{' '}
                    <span style={{ color: colors.grey[100] }}>{booking?.passenger?.passport}</span>
                  </Typography>
                </Stack>

                <Stack spacing={1} flex={1}>
                  <Typography variant="h3" fontWeight={'bold'} color={colors.grey[100]}>
                    Guide Details
                  </Typography>
                  <Divider variant="fullWidth" />

                  <Typography variant="h5" color={colors.greenAccent[500]} display="flex" alignItems="center" gap="5px">
                    <PersonIcon fontSize="small" /> Name:{' '}
                    <span style={{ color: colors.grey[100] }}>{`${booking?.guide?.name || ''}`}</span>
                  </Typography>
                  <Typography variant="h5" color={colors.greenAccent[500]} display="flex" alignItems="center" gap="5px">
                    <MailOutlineIcon fontSize="small" /> Email:{' '}
                    <span style={{ color: colors.grey[100] }}>{booking?.guide?.email}</span>
                  </Typography>
                  <Typography variant="h5" color={colors.greenAccent[500]} display="flex" alignItems="center" gap="5px">
                    <LocalPhoneIcon fontSize="small" /> Phone:{' '}
                    <span style={{ color: colors.grey[100] }}>{booking?.guide?.phone}</span>
                  </Typography>
                  <Typography variant="h5" color={colors.greenAccent[500]} display="flex" alignItems="center" gap="5px">
                    <BadgeIcon fontSize="small" /> License:{' '}
                    <span style={{ color: colors.grey[100] }}>{booking?.guide?.license}</span>
                  </Typography>
                  <Typography variant="h5" color={colors.greenAccent[500]} display="flex" alignItems="center" gap="5px">
                    <DirectionsBusIcon fontSize="small" /> Vehicle No:{' '}
                    <span style={{ color: colors.grey[100] }}>{booking?.guide?.vehicleNo}</span>
                  </Typography>
                  <Typography variant="h5" color={colors.greenAccent[500]} display="flex" alignItems="center" gap="5px">
                    <PlaceIcon fontSize="small" /> Address:{' '}
                    <span style={{ color: colors.grey[100] }}>{booking?.guide?.address}</span>
                  </Typography>
                </Stack>
              </Box>

              {/* Packages Section - same layout as Excursions */}
              <Stack spacing={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h3" fontWeight={'bold'} color={colors.grey[100]}>
                    Packages
                  </Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => {
                      openAddPackageDialog();
                    }}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Add Package
                  </Button>
                </Box>
                <Divider variant="fullWidth" />
                {!booking?.packages?.length && !booking?.packageId ? (
                  <Typography variant="body1" component="i" color={colors.grey[400]} sx={{ py: 1 }}>
                    No packages selected
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      '& .MuiDataGrid-root': { border: 'none' },
                      '& .MuiDataGrid-cell': { borderBottom: 'none' },
                      '& .MuiDataGrid-columnHeaders': { backgroundColor: colors.blueAccent[700], borderBottom: 'none' },
                      '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
                      '& .MuiDataGrid-footerContainer': { borderTop: 'none', backgroundColor: colors.blueAccent[700] },
                    }}
                  >
                    <DataGrid
                      rows={
                        booking?.packages?.length
                          ? booking.packages.map((p, idx) => ({
                            id: p.packageId ?? idx,
                            packageIdRaw: p.packageId,
                            packageGratisEditable: true,
                            isGratis: Number(p.isGratis) === 1 ? 1 : 0,
                            image: p.packageImage,
                            name: p.packageName,
                            description: p.packageDescription || '-',
                            price:
                              p.packagePrice != null && p.packagePrice !== ''
                                ? `€${Number(p.packagePrice).toFixed(2)}`
                                : '-',
                            excursionCount: p.excursionCount ?? 0,
                            totalQuantity: p.totalQuantity ?? 0,
                            paymentLabel: (() => {
                              const s = p.packagePaymentStatus;
                              if (s === 1 || s === true || String(s).toLowerCase() === 'paid') return 'Paid';
                              if (s === 0 || s === false || String(s).toLowerCase() === 'unpaid') return 'Unpaid';
                              if (typeof s === 'string' && s) return s === 'paid' ? 'Paid' : 'Unpaid';
                              return 'Unpaid';
                            })(),
                          }))
                          : [
                            {
                              id: booking.packageId,
                              packageIdRaw: booking.packageId,
                              packageGratisEditable: false,
                              isGratis: 0,
                              image: booking.packageImage,
                              name: booking.packageName,
                              description: booking.packageDescription || '-',
                              price:
                                booking.packagePrice != null && booking.packagePrice !== ''
                                  ? `€${Number(booking.packagePrice).toFixed(2)}`
                                  : '-',
                              excursionCount: 0,
                              totalQuantity: 0,
                              paymentLabel: (() => {
                                const s = booking.paymentStatus;
                                if (s === 1 || s === true || String(s).toLowerCase() === 'paid') return 'Paid';
                                if (s === 0 || s === false || String(s).toLowerCase() === 'unpaid') return 'Unpaid';
                                if (typeof s === 'string' && s) return s === 'paid' ? 'Paid' : 'Unpaid';
                                return 'Unpaid';
                              })(),
                            },
                          ]
                      }
                      columns={[
                        {
                          field: 'image',
                          headerName: 'Image',
                          width: 90,
                          sortable: false,
                          filterable: false,
                          renderCell: (p) => renderGridThumbnail({ ...p.row, image: p.value }, <BookmarkIcon />),
                        },
                        { field: 'name', headerName: 'Name', flex: 1, minWidth: 140 },
                        { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
                        { field: 'price', headerName: 'Price', width: 100 },
                        {
                          field: 'excursionCount',
                          headerName: 'Excursions',
                          width: 110,
                          type: 'number',
                          align: 'right',
                          headerAlign: 'right',
                        },
                        {
                          field: 'totalQuantity',
                          headerName: 'Quantity',
                          width: 100,
                          type: 'number',
                          align: 'right',
                          headerAlign: 'right',
                        },
                        {
                          field: 'paymentLabel',
                          headerName: 'Payment',
                          width: 140,
                          sortable: false,
                          renderCell: (p) => (
                            <Chip
                              label={p.value === 'Paid' ? 'Paid' : 'Unpaid'}
                              color={p.value === 'Paid' ? 'success' : 'default'}
                              size="small"
                              variant="outlined"
                            />
                          ),
                        },
                        {
                          field: 'isGratis',
                          headerName: 'Gratis',
                          width: 100,
                          sortable: false,
                          filterable: false,
                          renderCell: (cellValues) => {
                            const editable = cellValues.row.packageGratisEditable !== false;
                            const checked = Number(cellValues.row.isGratis) === 1;
                            const cb = (
                              <Checkbox
                                checked={checked}
                                disabled={!editable}
                                onChange={() =>
                                  handleTogglePackageGratis(
                                    booking?.bookingId || booking?.id,
                                    cellValues.row.packageIdRaw,
                                    checked,
                                    editable
                                  )
                                }
                                sx={{
                                  color: colors.grey[400],
                                  '&.Mui-checked': { color: colors.greenAccent[500] },
                                }}
                              />
                            );
                            return editable ? (
                              cb
                            ) : (
                              <Tooltip title="Package gratis applies to bookings stored in booking_packages. Re-save packages to use this toggle.">
                                <span>{cb}</span>
                              </Tooltip>
                            );
                          },
                        },
                        {
                          field: 'delete',
                          headerName: 'Delete',
                          width: 90,
                          sortable: false,
                          filterable: false,
                          renderCell: (cellValues) => (
                            <IconButton
                              color="error"
                              size="small"
                              disabled={addPackageLoading}
                              onClick={() => openDeletePackageDialog(cellValues.row.packageIdRaw)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          ),
                        },
                      ]}
                      autoHeight
                      hideFooter
                    />
                  </Box>
                )}
              </Stack>

              <Stack spacing={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h3" fontWeight={'bold'} color={colors.grey[100]}>
                    Excursions
                  </Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => {
                      getAvailableExcursions();
                      setSelectedExcursions([]);
                      setSelectedExcursionQtyById({});
                      setAddExcursionDialogOpen(true);
                    }}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Add Excursion
                  </Button>
                </Box>
                <Divider variant="fullWidth" />

                {!booking?.excursions || booking.excursions.length === 0 ? (
                  <Typography variant="p" component="i">
                    No Excursions found
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      '& .MuiDataGrid-root': { border: 'none' },
                      '& .MuiDataGrid-cell': { borderBottom: 'none' },
                      '& .MuiDataGrid-columnHeaders': { backgroundColor: colors.blueAccent[700], borderBottom: 'none' },
                      '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
                      '& .MuiDataGrid-footerContainer': { borderTop: 'none', backgroundColor: colors.blueAccent[700] },
                    }}
                  >
                    <DataGrid
                      rows={booking.excursions.map((e) => ({ ...e, id: e.excursionId }))}
                      columns={[
                        {
                          field: 'image',
                          headerName: 'Image',
                          width: 90,
                          sortable: false,
                          filterable: false,
                          renderCell: (p) => renderGridThumbnail({ ...p.row, image: p.value }),
                        },
                        {
                          field: 'name',
                          headerName: 'Name',
                          flex: 1,
                          minWidth: 220,
                          sortable: false,
                          renderCell: (p) => {
                            const raw = p.row.packageName || '';
                            const labels = raw
                              .split(',')
                              .map((s) => s.trim())
                              .filter(Boolean);
                            return (
                              <Stack
                                direction="row"
                                alignItems="center"
                                flexWrap="wrap"
                                useFlexGap
                                sx={{
                                  py: 0.75,
                                  columnGap: 1,
                                  rowGap: 0.75,
                                  width: '100%',
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  component="span"
                                  sx={{ color: colors.grey[100], flexShrink: 0 }}
                                >
                                  {p.row.name}
                                </Typography>
                                {labels.map((label) => (
                                  <Chip
                                    key={`${p.row.excursionId}-${label}`}
                                    label={label}
                                    size="small"
                                    sx={{
                                      height: 26,
                                      maxWidth: 200,
                                      fontSize: '0.72rem',
                                      fontWeight: 700,
                                      letterSpacing: '0.02em',
                                      borderRadius: '8px',
                                      backgroundColor: alpha(colors.blueAccent[500], 0.12),
                                      color: colors.blueAccent[100],
                                      border: `1px solid ${alpha(colors.blueAccent[400], 0.45)}`,
                                      boxShadow: `0 1px 3px ${alpha('#000', 0.35)}`,
                                      backdropFilter: 'blur(6px)',
                                      '& .MuiChip-label': {
                                        px: 1.1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      },
                                    }}
                                  />
                                ))}
                              </Stack>
                            );
                          },
                        },
                        { field: 'location', headerName: 'Location', flex: 1, minWidth: 160 },
                        {
                          field: 'quantity',
                          headerName: 'Qty',
                          width: 70,
                          type: 'number',
                          align: 'right',
                          headerAlign: 'right',
                          valueGetter: (params) => params.row.quantity ?? 1,
                        },
                        {
                          field: 'scanStatus',
                          headerName: 'Scan',
                          width: 140,
                          sortable: false,
                          renderCell: (p) => {
                            const scanned = Number(p.row.scanStatus || 0);
                            const total = Number(p.row.quantity || 1);
                            const isFullyScanned = scanned >= total;
                            const isPartiallyScanned = scanned > 0 && scanned < total;

                            return (
                              <Chip
                                icon={scanned > 0 ? <QrCode /> : <VisibilityOff />}
                                label={`${scanned} / ${total} Scanned`}
                                color={isFullyScanned ? 'success' : isPartiallyScanned ? 'warning' : 'default'}
                                size="small"
                              />
                            );
                          },
                        },
                        {
                          field: 'isGratis',
                          headerName: 'Gratis',
                          width: 100,
                          sortable: false,
                          filterable: false,
                          renderCell: (cellValues) => {
                            const viaPkg = Number(cellValues.row.gratisViaPackage) === 1;
                            const effective =
                              Number(cellValues.row.effectiveGratis) === 1 ||
                              cellValues.row.isGratis === 1 ||
                              cellValues.row.isGratis === true;
                            const raw = cellValues.row.isGratis === 1 || cellValues.row.isGratis === true;
                            const cb = (
                              <Checkbox
                                checked={effective}
                                disabled={viaPkg}
                                onChange={() =>
                                  handleToggleGratis(
                                    booking?.bookingId || booking?.id,
                                    cellValues.row.excursionId,
                                    raw
                                  )
                                }
                                sx={{
                                  color: colors.grey[400],
                                  '&.Mui-checked': {
                                    color: colors.greenAccent[500],
                                  },
                                }}
                              />
                            );
                            return viaPkg ? (
                              <Tooltip title="Complimentary because the package is marked Gratis. Change it under Packages.">
                                <span>{cb}</span>
                              </Tooltip>
                            ) : (
                              cb
                            );
                          },
                        },
                        {
                          field: 'actions',
                          headerName: 'View',
                          width: 90,
                          sortable: false,
                          filterable: false,
                          renderCell: (cellValues) => (
                            <Link to={`/reports/view/${cellValues.row.excursionId}`}>
                              <IconButton
                                size="small"
                                title="View report"
                                sx={{
                                  backgroundColor: colors.blueAccent[600],
                                  color: colors.grey[100],
                                  borderRadius: 2,
                                  '&:hover': { backgroundColor: colors.blueAccent[700] },
                                }}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Link>
                          ),
                        },
                        {
                          field: 'delete',
                          headerName: 'Delete',
                          width: 90,
                          sortable: false,
                          filterable: false,
                          renderCell: (cellValues) => (
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => {
                                setExcursionToDelete(cellValues.row.excursionId);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          ),
                        },
                      ]}
                      autoHeight
                      pageSize={10}
                      rowsPerPageOptions={[10, 25, 50]}
                    />
                  </Box>
                )}
              </Stack>

              {booking?.cart_summary && (
                <Box display="flex" flexDirection="column" gap="10px" sx={{ mt: 1 }}>
                  <Typography variant="h3" fontWeight={'bold'} color={colors.grey[100]} display="flex" alignItems="center" gap={1}>
                    <PaidIcon fontSize="small" /> Cart summary
                  </Typography>
                  <Divider variant="fullWidth" />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={2}>
                      <Typography variant="body2" color={colors.greenAccent[500]}>
                        Package amount
                      </Typography>
                      <Typography variant="h5" color={colors.grey[100]} fontWeight={600}>
                        €{' '}
                        {Number(booking.cart_summary.package_amount || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Typography variant="body2" color={colors.greenAccent[500]}>
                        Excursion amount
                      </Typography>
                      <Typography variant="h5" color={colors.grey[100]} fontWeight={600}>
                        €{' '}
                        {Number(booking.cart_summary.excursion_amount || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Typography variant="body2" color={colors.greenAccent[500]}>
                        Discount
                      </Typography>
                      <Typography variant="h5" color={colors.grey[100]} fontWeight={600}>
                        €{' '}
                        {Number(booking.cart_summary.manual_discount || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Typography variant="body2" color={colors.greenAccent[500]}>
                        Total paid
                      </Typography>
                      <Typography variant="h5" color={colors.grey[100]} fontWeight={600}>
                        €{' '}
                        {Number(booking.cart_summary.total_paid || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={12} md={4}>
                      <Typography variant="body2" color={colors.greenAccent[500]}>
                        Total to pay
                      </Typography>
                      <Typography variant="h5" color={colors.greenAccent[500]} fontWeight={700}>
                        €{' '}
                        {Number(booking.cart_summary.total_to_pay || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
      <ConfirmDeleteDialog
        open={deletePackageDialogOpen}
        onClose={closeDeletePackageDialog}
        onConfirm={confirmDeletePackage}
        title="Remove package from booking?"
        message="Are you sure you want to remove this package from the booking?"
        colors={colors}
        isLoading={addPackageLoading}
      />
      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={() => setOpenToast(false)}
        message={toastMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.primary[500],
            borderRadius: 2,
            border: `1px solid ${colors.grey[700]}`,
            boxShadow: '0 16px 48px rgba(0,0,0,0.35)',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.primary[500],
            color: colors.grey[100],
            fontWeight: 700,
            fontSize: '1.15rem',
            px: 3,
            pt: 2.5,
            pb: 1,
          }}
        >
          Delete Excursion?
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: colors.primary[500],
            px: 3,
            pt: 2.5,
            pb: 1,
          }}
        >
          <Typography variant="body1" color={colors.grey[100]} sx={{ lineHeight: 1.6, mb: 1.5 }}>
            Are you sure you want to delete this excursion?
          </Typography>
          <Typography variant="body2" color={colors.grey[400]} sx={{ lineHeight: 1.55 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: colors.primary[500],
            px: 3,
            py: 2,
            gap: 1.5,
            justifyContent: 'flex-end',
          }}
        >
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{
              borderColor: colors.grey[600],
              color: colors.grey[200],
              fontWeight: 600,
              borderRadius: 2,
              px: 2.5,
              py: 0.75,
              textTransform: 'none',
              '&:hover': {
                borderColor: colors.grey[400],
                backgroundColor: 'rgba(255,255,255,0.04)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              await handleRemoveExcursion(excursionToDelete);
              setDeleteDialogOpen(false);
            }}
            variant="contained"
            color="error"
            sx={{
              fontWeight: 600,
              borderRadius: 2,
              px: 2.5,
              py: 0.75,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none',
                opacity: 0.92,
              },
            }}
          >
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Package Dialog — same pattern as Add Excursion */}
      <Dialog
        open={addPackageDialogOpen}
        onClose={() => !addPackageLoading && setAddPackageDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: '#2c2c2c',
            backgroundImage: 'none',
            color: 'white',
            borderRadius: '4px',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#3f51b5',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '1.2rem',
            py: 2,
          }}
        >
          Add Package to Booking
        </DialogTitle>
        <DialogContent sx={{ mt: 1, mb: 2, px: 3 }}>
          <Typography
            variant="h4"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              mb: 3,
              mt: 1,
              letterSpacing: '0.5px',
            }}
          >
            Packages
          </Typography>
          {availablePackages.length > 0 ? (
            <Stack spacing={2}>
              {availablePackages.map((pkg) => {
                const isChecked = selectedPackageIdsForDialog.some((sid) => Number(sid) === Number(pkg.PackageID));
                return (
                  <Box key={pkg.PackageID} display="flex" alignItems="center" gap={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleTogglePackageInDialog(pkg.PackageID)}
                          sx={{
                            color: colors.grey[400],
                            '&.Mui-checked': {
                              color: colors.blueAccent[200],
                            },
                            '& .MuiSvgIcon-root': { fontSize: 26 },
                            p: 1.5,
                            mr: 1,
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '50%',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.12)',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="h5"
                          sx={{
                            color: 'white',
                            fontWeight: 500,
                            fontSize: '1.1rem',
                          }}
                        >
                          {pkg.PackageName}
                        </Typography>
                      }
                      sx={{
                        ml: 0,
                        flex: 1,
                        '&:hover': {
                          '& .MuiCheckbox-root': {
                            backgroundColor: 'rgba(255, 255, 255, 0.12)',
                          },
                        },
                      }}
                    />
                    {isChecked && (
                      <TextField
                        label="Qty"
                        type="number"
                        value={selectedPackageQtyById[pkg.PackageID] || 1}
                        onChange={(e) =>
                          setSelectedPackageQtyById((prev) => ({
                            ...prev,
                            [pkg.PackageID]: Math.max(1, Number(e.target.value || 1)),
                          }))
                        }
                        size="small"
                        inputProps={{ min: 1 }}
                        sx={{ width: 110 }}
                      />
                    )}
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body1" textAlign="center" py={2} sx={{ color: colors.grey[100] }}>
              No packages available for this destination.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => setAddPackageDialogOpen(false)}
            disabled={addPackageLoading}
            sx={{
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.9rem',
              letterSpacing: '1px',
              mr: 2,
            }}
          >
            CANCEL
          </Button>
          <Button
            onClick={handleSavePackagesFromDialog}
            variant="contained"
            disabled={addPackageLoading}
            sx={{
              backgroundColor: '#5a67ba',
              color: 'white !important',
              fontWeight: 'bold',
              px: 3,
              py: 1.2,
              fontSize: '0.9rem',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#4c59af',
              },
            }}
          >
            {addPackageLoading ? 'SAVING...' : 'SAVE SELECTED'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Excursion Dialog */}
      <Dialog
        open={addExcursionDialogOpen}
        onClose={() => setAddExcursionDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: '#2c2c2c', // Dark grey background from image
            backgroundImage: 'none',
            color: 'white',
            borderRadius: '4px',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#3f51b5', // Specific blue from image
            color: 'white',
            fontWeight: 'bold',
            fontSize: '1.2rem',
            py: 2,
          }}
        >
          Add Excursion to Booking
        </DialogTitle>
        <DialogContent sx={{ mt: 1, mb: 2, px: 3 }}>
          <Typography
            variant="h4"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              mb: 3,
              mt: 1,
              letterSpacing: '0.5px',
            }}
          >
            Excursions
          </Typography>
          {availableExcursions.length > 0 ? (
            <Stack spacing={2}>
              {availableExcursions.map((excursion) => {
                const isChecked = selectedExcursions.includes(excursion.ExcursionID);
                return (
                  <Box key={excursion.ExcursionID} display="flex" alignItems="center" gap={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleToggleExcursion(excursion.ExcursionID)}
                          sx={{
                            color: colors.grey[400],
                            '&.Mui-checked': {
                              color: colors.blueAccent[200],
                            },
                            '& .MuiSvgIcon-root': { fontSize: 26 },
                            p: 1.5,
                            mr: 1,
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '50%',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.12)',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="h5"
                          sx={{
                            color: 'white',
                            fontWeight: 500,
                            fontSize: '1.1rem',
                          }}
                        >
                          {excursion.ExcursionName}
                        </Typography>
                      }
                      sx={{
                        ml: 0,
                        flex: 1,
                        '&:hover': {
                          '& .MuiCheckbox-root': {
                            backgroundColor: 'rgba(255, 255, 255, 0.12)',
                          },
                        },
                      }}
                    />
                    {isChecked && (
                      <TextField
                        label="Qty"
                        type="number"
                        value={selectedExcursionQtyById[excursion.ExcursionID] || 1}
                        onChange={(e) =>
                          setSelectedExcursionQtyById((prev) => ({
                            ...prev,
                            [excursion.ExcursionID]: Math.max(1, Number(e.target.value || 1)),
                          }))
                        }
                        size="small"
                        inputProps={{ min: 1 }}
                        sx={{ width: 110 }}
                      />
                    )}
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body1" textAlign="center" py={2} sx={{ color: colors.grey[100] }}>
              No more excursions available to add for this destination and date.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => setAddExcursionDialogOpen(false)}
            sx={{
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.9rem',
              letterSpacing: '1px',
              mr: 2,
            }}
          >
            CANCEL
          </Button>
          <Button
            onClick={handleAddExcursion}
            variant="contained"
            disabled={selectedExcursions.length === 0}
            sx={{
              backgroundColor: selectedExcursions.length > 0 ? '#5a67ba' : '#444444',
              color: 'white !important',
              fontWeight: 'bold',
              px: 3,
              py: 1.2,
              fontSize: '0.9rem',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#4c59af',
              },
              '&.Mui-disabled': {
                backgroundColor: '#444444',
                color: '#888888 !important',
              },
            }}
          >
            ADD SELECTED
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BookingInformation;
